// frontend/src/features/seo/SeoAieoView.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../api/axios';
import SeoHeader from './components/SeoHeader';
import SeoKpiStrip from './components/SeoKpiStrip';
import TopicResearchTab from './components/TopicResearchTab';
import ContentBriefsTab from './components/ContentBriefsTab';
import OnPageAieoAuditTab from './components/OnPageAieoAuditTab';
import SchemaStudioModal from './components/SchemaStudioModal';
import ContentGraphTab, { isLegacyMockGraph } from './components/ContentGraphTab';
import ContentRefreshTab, { isLegacyMockRefreshQueue } from './components/ContentRefreshTab';
import VisibilityMonitorTab, { isLegacyMockVisibility } from './components/VisibilityMonitorTab';
import { isLegacyMockAudit } from './components/OnPageAieoAuditTab';
import GovernanceSettingsModal from './components/GovernanceSettingsModal';
import ContentStudioModal from './components/ContentStudioModal';
import './SeoAieoView.css';

const INITIAL_PAGE_AUDIT = null;
const INITIAL_VISIBILITY_METRICS = {
  timeSeries: [],
  recentCitations: [],
};
const INITIAL_GOVERNANCE_SETTINGS = {
  dailyPublishCap: 5,
  minSimilarityThreshold: 85,
  requireEditorialApproval: true,
  autoStageToCms: false,
  cmsProvider: '',
  cmsEndpoint: '',
  connectionStatus: 'disconnected',
  lastSyncAt: null,
};

export function isLegacyMockTopic(t) {
  if (!t || typeof t !== 'object') return false;
  if (t.isSampleDemo === true) return false;
  return (
    (t.id === 'tc-001' && t.topic === 'B2B Lead Enrichment') ||
    (t.id === 'tc-002' && t.topic === 'AI Answer Engine Optimization (AIEO)') ||
    (t.id === 'tc-003' && t.topic === 'Cold Email Deliverability Checklist')
  );
}

export function isLegacyMockBrief(b) {
  if (!b || typeof b !== 'object') return false;
  if (b.isSampleDemo === true) return false;
  return (
    (b.id === 'brief-001' && b.targetSlug === '/blog/aieo-guide-2026') ||
    (b.id === 'brief-002' && b.targetSlug === '/guides/b2b-lead-enrichment-comparison') ||
    b.targetSlug === '/blog/aieo-guide-2026' ||
    b.targetSlug === '/guides/b2b-lead-enrichment-comparison'
  );
}

export default function SeoAieoView() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('topics');
  const [topicClusters, setTopicClusters] = useState(() => {
    try {
      const saved = localStorage.getItem('lead_huntrix_seo_topic_clusters');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const cleaned = parsed.filter((c) => !isLegacyMockTopic(c));
          if (cleaned.length !== parsed.length) {
            localStorage.setItem('lead_huntrix_seo_topic_clusters', JSON.stringify(cleaned));
          }
          return cleaned;
        }
      }
      return [];
    } catch {
      return [];
    }
  });
  const [briefs, setBriefs] = useState(() => {
    try {
      const saved = localStorage.getItem('lead_huntrix_seo_content_briefs');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const cleaned = parsed.filter((b) => !isLegacyMockBrief(b));
          if (cleaned.length !== parsed.length) {
            localStorage.setItem('lead_huntrix_seo_content_briefs', JSON.stringify(cleaned));
          }
          return cleaned;
        }
      }
      return [];
    } catch {
      return [];
    }
  });
  const [pageAudit, setPageAudit] = useState(() => {
    try {
      const saved = localStorage.getItem('lead_huntrix_seo_page_audit');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (isLegacyMockAudit(parsed)) {
          localStorage.removeItem('lead_huntrix_seo_page_audit');
          return null;
        }
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  });

  const handleUpdatePageAudit = (updatedAudit) => {
    if (isLegacyMockAudit(updatedAudit)) {
      setPageAudit(null);
      try {
        localStorage.removeItem('lead_huntrix_seo_page_audit');
      } catch (e) {
        console.warn('Failed to remove legacy page audit from localStorage', e);
      }
      return;
    }
    setPageAudit(updatedAudit);
    try {
      if (updatedAudit && (updatedAudit.url || updatedAudit.targetTopic)) {
        localStorage.setItem('lead_huntrix_seo_page_audit', JSON.stringify(updatedAudit));
      } else {
        localStorage.removeItem('lead_huntrix_seo_page_audit');
      }
    } catch (e) {
      console.warn('Failed to save page audit to localStorage', e);
    }
  };
  const [schemaTemplates] = useState(null);
  const [contentGraph, setContentGraph] = useState(() => {
    try {
      const saved = localStorage.getItem('lead_huntrix_seo_content_graph');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (isLegacyMockGraph(parsed)) {
          localStorage.removeItem('lead_huntrix_seo_content_graph');
          return { nodes: [], edges: [], recommendedLinks: [] };
        }
        return parsed;
      }
      return { nodes: [], edges: [], recommendedLinks: [] };
    } catch {
      return { nodes: [], edges: [], recommendedLinks: [] };
    }
  });

  const [visibilityData, setVisibilityData] = useState(() => {
    try {
      const saved = localStorage.getItem('lead_huntrix_seo_visibility_data');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (isLegacyMockVisibility(parsed)) {
          localStorage.removeItem('lead_huntrix_seo_visibility_data');
          return { timeSeries: [], recentCitations: [] };
        }
        return parsed;
      }
      return INITIAL_VISIBILITY_METRICS;
    } catch {
      return INITIAL_VISIBILITY_METRICS;
    }
  });

  const handleUpdateVisibilityData = (updatedData) => {
    setVisibilityData(updatedData);
    try {
      localStorage.setItem('lead_huntrix_seo_visibility_data', JSON.stringify(updatedData));
    } catch (e) {
      console.warn('Failed to save visibility data to localStorage', e);
    }
    safeApiCall(() => axiosInstance.put('/seo/visibility', updatedData));
  };

  const [governanceSettings, setGovernanceSettings] = useState(INITIAL_GOVERNANCE_SETTINGS);
  const [auditDraftContext, setAuditDraftContext] = useState(null);

  // Hydrate entire SEO module from backend on mount (1-Table 1-Query)
  useEffect(() => {
    let isMounted = true;
    async function loadServerSeoState() {
      try {
        const response = await axiosInstance.get('/seo/state');
        if (response.data && response.data.success && response.data.data && isMounted) {
          const {
            governanceSettings: serverGov,
            contentGraph: serverGraph,
            visibilityData: serverVis,
            topicClusters: serverTopics,
            briefs: serverBriefs,
            refreshQueue: serverRefresh,
          } = response.data.data;

          if (serverGov) setGovernanceSettings(serverGov);
          if (serverGraph && Array.isArray(serverGraph.nodes) && serverGraph.nodes.length > 0) {
            setContentGraph(serverGraph);
          }
          if (serverVis) {
            if (isLegacyMockVisibility(serverVis)) {
              localStorage.removeItem('lead_huntrix_seo_visibility_data');
              setVisibilityData({ timeSeries: [], recentCitations: [] });
            } else {
              setVisibilityData(serverVis);
            }
          }
          if (Array.isArray(serverTopics)) {
            const cleanedTopics = serverTopics.filter((t) => !isLegacyMockTopic(t));
            setTopicClusters(cleanedTopics);
          }
          if (Array.isArray(serverBriefs)) {
            const cleanedBriefs = serverBriefs.filter((b) => !isLegacyMockBrief(b));
            setBriefs(cleanedBriefs);
          }
          if (Array.isArray(serverRefresh)) {
            const cleanedRefresh = serverRefresh.filter((r) => !isLegacyMockRefreshQueue([r]));
            setRefreshQueue(cleanedRefresh);
          }
        }
      } catch (err) {
        console.warn(
          '[SeoAieoView] Hydration from /seo/state failed, keeping initial state:',
          err.message
        );
      }
    }
    loadServerSeoState();
    return () => {
      isMounted = false;
    };
  }, []);

  const safeApiCall = (call) => {
    try {
      const p = call();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          console.warn('[SeoAieoView] API persistence warning:', err?.message || err);
        });
      }
    } catch (err) {
      console.warn('[SeoAieoView] API invocation warning:', err?.message || err);
    }
  };

  const handleUpdateContentGraph = (updatedGraph) => {
    setContentGraph(updatedGraph);
    try {
      localStorage.setItem('lead_huntrix_seo_content_graph', JSON.stringify(updatedGraph));
    } catch (e) {
      console.warn('Failed to save content graph to localStorage', e);
    }
    safeApiCall(() => axiosInstance.put('/seo/graph', updatedGraph));
  };

  const [refreshQueue, setRefreshQueue] = useState(() => {
    try {
      const saved = localStorage.getItem('lead_huntrix_seo_refresh_queue');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (isLegacyMockRefreshQueue(parsed)) {
          localStorage.removeItem('lead_huntrix_seo_refresh_queue');
          return [];
        }
        return parsed;
      }
      return [];
    } catch {
      return [];
    }
  });

  const handleUpdateRefreshQueue = (updatedQueue) => {
    setRefreshQueue(updatedQueue);
    try {
      localStorage.setItem('lead_huntrix_seo_refresh_queue', JSON.stringify(updatedQueue));
    } catch (e) {
      console.warn('Failed to save refresh queue to localStorage', e);
    }
  };

  const handleApplyRefresh = (taskId, extraData = {}) => {
    setRefreshQueue((prev) => {
      const updated = prev.map((t) =>
        t.id === taskId
          ? {
              ...t,
              status: 'applied',
              trafficDecay: '0% (Restored)',
              approvedAt: new Date().toISOString(),
              approvedBy: 'Current Editorial Team',
              ...(extraData?.taskUpdates || {}),
            }
          : t
      );
      try {
        localStorage.setItem('lead_huntrix_seo_refresh_queue', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save refresh queue to localStorage', e);
      }
      return updated;
    });
    safeApiCall(() => axiosInstance.put(`/seo/refresh/${taskId}/apply`, extraData));
  };

  const handleSaveGovernance = (newSettings) => {
    setGovernanceSettings(newSettings);
    safeApiCall(() => axiosInstance.put('/seo/governance', newSettings));
  };

  const handleAddCluster = (newCluster) => {
    setTopicClusters((prev) => {
      const exists = prev.some((c) => c.topic.toLowerCase() === newCluster.topic.toLowerCase());
      if (exists) return prev;
      const updated = [newCluster, ...prev];
      try {
        localStorage.setItem('lead_huntrix_seo_topic_clusters', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save topic clusters to localStorage', e);
      }
      return updated;
    });
    safeApiCall(() => axiosInstance.post('/seo/topics', { cluster: newCluster }));
  };

  const handleDeleteCluster = (clusterId) => {
    setTopicClusters((prev) => {
      const updated = prev.filter((c) => c.id !== clusterId);
      try {
        localStorage.setItem('lead_huntrix_seo_topic_clusters', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save topic clusters to localStorage', e);
      }
      return updated;
    });
    safeApiCall(() => axiosInstance.delete(`/seo/topics/${clusterId}`));
  };

  const handleClearClusters = () => {
    setTopicClusters([]);
    try {
      localStorage.removeItem('lead_huntrix_seo_topic_clusters');
    } catch (e) {
      console.warn('Failed to clear topic clusters from localStorage', e);
    }
    safeApiCall(() => axiosInstance.delete('/seo/topics'));
  };

  const handleAddBrief = (newBrief) => {
    setBriefs((prev) => {
      const updated = [newBrief, ...prev];
      try {
        localStorage.setItem('lead_huntrix_seo_content_briefs', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save content briefs to localStorage', e);
      }
      return updated;
    });
    safeApiCall(() => axiosInstance.post('/seo/briefs', newBrief));
  };

  const handleUpdateBrief = (updatedBrief) => {
    setBriefs((prev) => {
      const exists = prev.some((b) => b.id === updatedBrief.id);
      const updated = exists
        ? prev.map((b) => (b.id === updatedBrief.id ? updatedBrief : b))
        : [updatedBrief, ...prev];
      try {
        localStorage.setItem('lead_huntrix_seo_content_briefs', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save content briefs to localStorage', e);
      }
      return updated;
    });
    safeApiCall(() => axiosInstance.put(`/seo/briefs/${updatedBrief.id}`, updatedBrief));
  };

  const handleUpdateBriefContent = async (briefId, newContent) => {
    let updatedTargetBrief = null;
    const wordCount = newContent ? newContent.trim().split(/\s+/).filter(Boolean).length : 0;
    setBriefs((prev) => {
      const updated = prev.map((b) => {
        if (b.id === briefId) {
          updatedTargetBrief = {
            ...b,
            content: newContent,
            draftArticle: {
              ...(b.draftArticle || {}),
              markdown: newContent,
              wordCount: wordCount || b.draftArticle?.wordCount || 0,
              updatedAt: new Date().toISOString(),
            },
          };
          return updatedTargetBrief;
        }
        return b;
      });
      try {
        localStorage.setItem('lead_huntrix_seo_content_briefs', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save content briefs to localStorage', e);
      }
      return updated;
    });

    setActiveStudioBrief((prev) =>
      prev && prev.id === briefId
        ? {
            ...prev,
            content: newContent,
            draftArticle: {
              ...(prev.draftArticle || {}),
              markdown: newContent,
              wordCount: wordCount || prev.draftArticle?.wordCount || 0,
              updatedAt: new Date().toISOString(),
            },
          }
        : prev
    );

    await safeApiCall(() => axiosInstance.patch(`/seo/briefs/${briefId}`, { content: newContent }));
    return updatedTargetBrief;
  };

  const handleApproveBrief = (briefId) => {
    setBriefs((prev) => {
      const updated = prev.map((b) =>
        b.id === briefId
          ? {
              ...b,
              status: 'approved',
              approvedBy: 'Current Editor',
              approvedAt: new Date().toISOString(),
            }
          : b
      );
      try {
        localStorage.setItem('lead_huntrix_seo_content_briefs', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save content briefs to localStorage', e);
      }
      return updated;
    });
    safeApiCall(() => axiosInstance.post(`/seo/briefs/${briefId}/approve`));
  };

  const handleDeleteBrief = (briefId) => {
    setBriefs((prev) => {
      const updated = prev.filter((b) => b.id !== briefId);
      try {
        localStorage.setItem('lead_huntrix_seo_content_briefs', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save content briefs to localStorage', e);
      }
      return updated;
    });
    safeApiCall(() => axiosInstance.delete(`/seo/briefs/${briefId}`));
  };

  const handleClearBriefs = () => {
    setBriefs([]);
    try {
      localStorage.removeItem('lead_huntrix_seo_content_briefs');
    } catch (e) {
      console.warn('Failed to clear content briefs from localStorage', e);
    }
    safeApiCall(() => axiosInstance.delete('/seo/briefs'));
  };

  const kpis = {
    organicImpressions: '0',
    impressionsTrend: '0%',
    aiCitations: visibilityData.recentCitations?.length || 0,
    aiCitationsTrend: '0%',
    topicGapsIdentified: topicClusters.length > 0 ? topicClusters.length * 3 : 0,
    activeBriefs: briefs.length,
    pagesRequiringRefresh: refreshQueue.filter((t) => t.status !== 'applied').length,
    averageAieoScore: 0,
  };

  // Modal controls
  const [isGovernanceOpen, setIsGovernanceOpen] = useState(false);
  const [isCreateBriefOpen, setIsCreateBriefOpen] = useState(false);
  const [activeStudioBrief, setActiveStudioBrief] = useState(null);
  const [studioScrollToSchema, setStudioScrollToSchema] = useState(false);
  const [attentionBriefId, setAttentionBriefId] = useState(null);
  const [activeSchemaBriefId, setActiveSchemaBriefId] = useState('');
  // Schema.org Studio modal (opened from a brief or the publish flow). When
  // opened via publish, we record the brief so we can reopen Content Studio
  // after Auto Update injects the schema.
  const [isSchemaModalOpen, setIsSchemaModalOpen] = useState(false);
  const [schemaModalSeedMode, setSchemaModalSeedMode] = useState(null);
  const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
  const [isEnhancingBrief, setIsEnhancingBrief] = useState(false);
  const [newBriefTitle, setNewBriefTitle] = useState('');
  const [newBriefSlug, setNewBriefSlug] = useState('');
  const [newBriefAudience, setNewBriefAudience] = useState('');

  const handleOpenNewBrief = (initialTitle = '') => {
    if (typeof initialTitle === 'string' && initialTitle.trim()) {
      const trimmed = initialTitle.trim();
      setNewBriefTitle(trimmed);
      setNewBriefSlug(
        `/blog/${trimmed
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')}`
      );
    } else {
      setNewBriefTitle('');
      setNewBriefSlug('');
    }
    setIsCreateBriefOpen(true);
  };

  // When user attempts to publish an unapproved brief from Content Studio, redirect to Content Briefs tab to approve
  const handleRequireApprovalFromStudio = (brief) => {
    setActiveStudioBrief(null);
    setActiveTab('briefs');
    setAttentionBriefId(brief?.id || null);
  };

  // Handle direct handoff from Content Briefs to Schema.org Studio (opens modal)
  const handleOpenSchemaForBrief = (brief) => {
    if (brief?.id) {
      setActiveSchemaBriefId(brief.id);
    }
    setSchemaModalSeedMode(null);
    setIsSchemaModalOpen(true);
  };

  // Publish flow: Content Studio detected no JSON-LD schema and the user chose a
  // seeding mode. Close the studio and open the Schema modal; after Auto Update
  // we reopen the studio (now with schema injected).
  const handleStartSchemaFlow = (brief, mode) => {
    if (!brief?.id) return;
    setActiveStudioBrief(null);
    setStudioScrollToSchema(false);
    setActiveSchemaBriefId(brief.id);
    setSchemaModalSeedMode(mode || 'manual');
    setIsSchemaModalOpen(true);
  };

  // Auto Update injected the schema and the modal is closing. Reopen Content
  // Studio for the same brief (now with schema present) so the user continues
  // from the article — whether they came from Step 2 or the publish flow.
  const handleSchemaAutoUpdated = (updatedBrief) => {
    setIsSchemaModalOpen(false);
    setSchemaModalSeedMode(null);
    if (updatedBrief) {
      setActiveTab('briefs');
      setActiveStudioBrief(updatedBrief);
      setStudioScrollToSchema(true);
    }
  };

  const handleCloseSchemaModal = () => {
    setIsSchemaModalOpen(false);
    setSchemaModalSeedMode(null);
  };

  // Handle direct handoff from Step 2 (Content Briefs) to Step 3 (On-Page SEO & AIEO Audit)
  const handleAuditDraft = (brief) => {
    if (!brief) return;

    // Find linked topic cluster
    let matchedCluster = null;
    if (brief.topicClusterId) {
      matchedCluster = topicClusters.find((c) => c.id === brief.topicClusterId);
    }
    if (!matchedCluster) {
      matchedCluster = topicClusters.find(
        (c) =>
          c.topic.toLowerCase() === brief.title.toLowerCase() ||
          brief.title.toLowerCase().includes(c.topic.toLowerCase())
      );
    }

    const outlineMd = Array.isArray(brief.outline)
      ? brief.outline
          .map((o) => `${'#'.repeat(o.level === 'H1' ? 1 : o.level === 'H2' ? 2 : 3)} ${o.text}`)
          .join('\n\n')
      : '';
    const proofMd =
      Array.isArray(brief.proofPoints) && brief.proofPoints.length > 0
        ? `\n\n### Verified Proof Points & Citations\n${brief.proofPoints.map((p) => `- ${p}`).join('\n')}`
        : '';
    const ctaMd = brief.cta ? `\n\n> **Primary Call to Action:** ${brief.cta}` : '';

    const draftMarkdown = `# ${brief.title}\n\n${outlineMd}${proofMd}${ctaMd}`;

    setAuditDraftContext({
      targetTopic: matchedCluster?.topic || brief.title,
      searchIntent: brief.searchIntent || matchedCluster?.primaryIntent || 'commercial',
      inputType: 'draft',
      draftText: draftMarkdown,
      url: `https://leadhuntrix.com${brief.targetSlug || '/blog/article'}`,
      cluster: matchedCluster || null,
      briefId: brief.id,
      timestamp: Date.now(),
    });

    setActiveTab('audit');
  };

  // Handle generating a new brief from a topic cluster click — INSTANT by default (0ms)
  const handleCreateBriefFromTopic = (cluster) => {
    const slugName = cluster.topic
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    const questionsList =
      Array.isArray(cluster.relatedQuestions) && cluster.relatedQuestions.length > 0
        ? cluster.relatedQuestions.slice(0, 3).map((q, i) => {
            const qText =
              typeof q === 'string'
                ? q
                : q?.question || q?.title || q?.name || q?.q || q?.text || '';
            return { level: 'H2', text: `${i + 2}. ${qText}` };
          })
        : [
            { level: 'H2', text: '2. Strategic Implementation Checklist' },
            { level: 'H2', text: '3. Common Pitfalls and Solutions' },
          ];

    const instantBrief = {
      id: `brief-${Date.now()}`,
      title: cluster.topic,
      targetSlug: `/blog/${slugName}`,
      topicClusterId: cluster.id,
      targetAudience: 'Target buyers, Sales Ops, and B2B leaders',
      searchIntent: cluster.primaryIntent || 'informational',
      conversionObjective: 'Book Demo via Lead Huntrix',
      targetWordCount: 2200,
      status: 'under_review',
      approvedBy: null,
      approvedAt: null,
      outline: [
        { level: 'H1', text: cluster.topic },
        { level: 'H2', text: '1. Why It Matters' },
        ...questionsList,
        { level: 'H2', text: `${questionsList.length + 2}. Practical Implementation Checklist` },
        { level: 'H2', text: `${questionsList.length + 3}. Key Takeaways & Recommendations` },
      ],
      targetEntities: cluster.entities || ['B2B Sales', 'Lead Gen'],
      proofPoints:
        Array.isArray(cluster.competitorGaps) && cluster.competitorGaps.length > 0
          ? cluster.competitorGaps.map((g) => `Verified differentiator: ${g}`)
          : ['Directly validated against SERP research data'],
      internalLinks: [{ page: '/features/campaign', anchorText: 'outreach campaigns' }],
      cta: 'Get started with verified B2B lead generation',
      isAIGenerated: false,
    };

    handleAddBrief(instantBrief);
    setActiveTab('briefs');
  };

  // Handle on-demand AI enhancement/synthesis for a selected brief
  const handleEnhanceWithAI = async (briefToEnhance) => {
    if (!briefToEnhance) return;
    setIsEnhancingBrief(true);
    try {
      const response = await axiosInstance.post('/seo/briefs/generate', {
        topic: briefToEnhance.title,
        targetAudience: briefToEnhance.targetAudience,
        searchIntent: briefToEnhance.searchIntent,
        targetWordCount: briefToEnhance.targetWordCount,
      });

      if (response.data && response.data.success && response.data.data) {
        const enhanced = {
          ...briefToEnhance,
          ...response.data.data,
          id: briefToEnhance.id,
          targetSlug: briefToEnhance.targetSlug,
          isAIGenerated: true,
          status: 'under_review',
          approvedBy: null,
          approvedAt: null,
        };
        handleUpdateBrief(enhanced);
      }
    } catch (err) {
      console.warn('[SeoAieoView] AI brief enhancement failed:', err.message);
    } finally {
      setIsEnhancingBrief(false);
    }
  };

  // Handle opening Content Studio workspace for a brief
  const handleOpenInStudio = (brief, options = {}) => {
    setActiveStudioBrief(brief);
    setStudioScrollToSchema(Boolean(options?.scrollToSchema));
  };

  // Reopen the Content Studio when the user returns from the Landing Page
  // Builder (navigated there from the studio with { openBriefId }). The draft
  // was saved before leaving, so the latest brief content is restored. The
  // navigation state is cleared afterwards so a refresh does not reopen it.
  useEffect(() => {
    const openBriefId = location.state?.openBriefId;
    if (!openBriefId) return;
    const target = briefs.find((b) => b.id === openBriefId);
    if (target) {
      setActiveTab('briefs');
      setActiveStudioBrief(target);
      setStudioScrollToSchema(false);
    }
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, briefs, navigate]);

  // Handle saving drafted article inside brief
  const handleSaveDraftArticle = (briefId, draftData) => {
    let updatedBrief = null;
    setBriefs((prev) => {
      const updated = prev.map((b) => {
        if (b.id === briefId) {
          // Merge so CMS publish metadata (cmsPostId, cmsPostUrl, ...) survives a
          // draft save; otherwise the next republish creates a new CMS post.
          updatedBrief = { ...b, draftArticle: { ...(b.draftArticle || {}), ...draftData } };
          return updatedBrief;
        }
        return b;
      });
      try {
        localStorage.setItem('lead_huntrix_seo_content_briefs', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save content briefs with draft article to localStorage', e);
      }
      return updated;
    });

    setActiveStudioBrief((prev) =>
      prev && prev.id === briefId
        ? { ...prev, draftArticle: { ...(prev.draftArticle || {}), ...draftData } }
        : prev
    );

    if (updatedBrief) {
      safeApiCall(() => axiosInstance.put(`/seo/briefs/${briefId}`, updatedBrief));
    }
  };

  // Link (or unlink) a landing page for the article's CTA (WF-04 / FR-M12-006).
  // Saved immediately so the server sees the link before publishing. The open
  // Content Studio brief is intentionally not replaced, so unsaved article
  // edits are kept.
  const handleLinkLandingPage = async (briefId, landingPageId) => {
    const current = briefs.find((b) => b.id === briefId);
    if (!current) return;
    const updatedBrief = { ...current, landingPageId: landingPageId || null };
    setBriefs((prev) => {
      const updated = prev.map((b) => (b.id === briefId ? updatedBrief : b));
      try {
        localStorage.setItem('lead_huntrix_seo_content_briefs', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save content briefs to localStorage', e);
      }
      return updated;
    });
    await axiosInstance.put(`/seo/briefs/${briefId}`, updatedBrief);
  };

  // Handle publishing approved brief draft to connected CMS (FR-M11-009)
  const handlePublishToCms = async (brief, draftMarkdown) => {
    if (!brief) return null;
    const effectiveMarkdown = draftMarkdown || brief.draftArticle?.markdown || '';
    try {
      const response = await axiosInstance.post(`/seo/briefs/${brief.id}/publish`, {
        overridePublishStatus: governanceSettings?.autoStageToCms ? 'draft' : 'publish',
        isSimulation: governanceSettings?.cmsEndpoint?.includes('example.com') || false,
        draftMarkdown: effectiveMarkdown,
        draftArticle: brief.draftArticle,
      });

      if (response && response.data && response.data.success && response.data.data) {
        const publishResult = { success: true, ...response.data.data };

        setBriefs((prev) => {
          const updated = prev.map((b) => {
            if (b.id === brief.id) {
              const updatedDraft = {
                ...(b.draftArticle || {}),
                markdown: draftMarkdown || b.draftArticle?.markdown,
                publishedAt: publishResult.publishedAt,
                publishedBy: publishResult.publishedBy,
                cmsProvider: publishResult.cmsProvider,
                cmsPostId: publishResult.cmsPostId,
                cmsPostUrl: publishResult.cmsPostUrl,
                cmsStatus: publishResult.cmsStatus,
              };
              return {
                ...b,
                draftArticle: updatedDraft,
                lastPublishedAt: publishResult.publishedAt,
              };
            }
            return b;
          });

          try {
            localStorage.setItem('lead_huntrix_seo_content_briefs', JSON.stringify(updated));
          } catch (e) {
            console.warn('Failed to save briefs with publish status to localStorage', e);
          }
          return updated;
        });

        setActiveStudioBrief((prev) => {
          if (!prev || prev.id !== brief.id) return prev;
          return {
            ...prev,
            draftArticle: {
              ...(prev.draftArticle || {}),
              markdown: draftMarkdown || prev.draftArticle?.markdown,
              publishedAt: publishResult.publishedAt,
              publishedBy: publishResult.publishedBy,
              cmsProvider: publishResult.cmsProvider,
              cmsPostId: publishResult.cmsPostId,
              cmsPostUrl: publishResult.cmsPostUrl,
              cmsStatus: publishResult.cmsStatus,
            },
            lastPublishedAt: publishResult.publishedAt,
          };
        });

        return publishResult;
      }
      return null;
    } catch (err) {
      console.error('[SeoAieoView] Publish to CMS failed:', err);
      throw err;
    }
  };

  // Handle direct handoff from Content Studio to Step 3 Audit
  const handleAuditFromStudio = (brief, draftMarkdown) => {
    if (!brief) return;

    let matchedCluster = null;
    if (brief.topicClusterId) {
      matchedCluster = topicClusters.find((c) => c.id === brief.topicClusterId);
    }
    if (!matchedCluster) {
      matchedCluster = topicClusters.find(
        (c) =>
          c.topic.toLowerCase() === brief.title.toLowerCase() ||
          brief.title.toLowerCase().includes(c.topic.toLowerCase())
      );
    }

    setAuditDraftContext({
      targetTopic: matchedCluster?.topic || brief.title,
      searchIntent: brief.searchIntent || matchedCluster?.primaryIntent || 'commercial',
      inputType: 'draft',
      draftText: draftMarkdown || `# ${brief.title}`,
      url: `https://leadhuntrix.com${brief.targetSlug || '/blog/article'}`,
      cluster: matchedCluster || null,
      briefId: brief.id,
      timestamp: Date.now(),
    });

    setActiveStudioBrief(null);
    setActiveTab('audit');
  };

  // Quick brief modal submit (supports 'manual' or 'ai')
  const handleSaveNewBrief = async (e, mode = 'manual') => {
    if (e && e.preventDefault) e.preventDefault();
    if (!newBriefTitle) return;

    const titleToUse = newBriefTitle.trim();
    const slugToUse = newBriefSlug
      ? newBriefSlug.startsWith('/')
        ? newBriefSlug.trim()
        : `/${newBriefSlug.trim()}`
      : `/blog/${titleToUse.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    const audienceToUse = newBriefAudience.trim() || 'B2B Growth Leaders';

    setIsCreateBriefOpen(false);
    setNewBriefTitle('');
    setNewBriefSlug('');
    setNewBriefAudience('');
    setActiveTab('briefs');

    if (mode === 'ai') {
      setIsGeneratingBrief(true);
      try {
        const response = await axiosInstance.post('/seo/briefs/generate', {
          topic: titleToUse,
          targetAudience: audienceToUse,
        });

        if (response.data && response.data.success && response.data.data) {
          const generated = {
            ...response.data.data,
            targetSlug: slugToUse,
          };
          handleAddBrief(generated);
          return;
        }
      } catch (err) {
        console.warn(
          '[SeoAieoView] AI brief generation failed, using draft template:',
          err.message
        );
      } finally {
        setIsGeneratingBrief(false);
      }
    }

    const newBrief = {
      id: `brief-${Date.now()}`,
      title: titleToUse,
      targetSlug: slugToUse,
      topicClusterId: null,
      targetAudience: audienceToUse,
      searchIntent: 'informational',
      conversionObjective: 'Sign up for Trial',
      targetWordCount: 1800,
      status: 'draft',
      approvedBy: null,
      approvedAt: null,
      outline: [
        { level: 'H1', text: titleToUse },
        { level: 'H2', text: 'Introduction & Core Definitions' },
        { level: 'H2', text: 'Step-by-Step Practical Blueprint' },
        { level: 'H2', text: 'Summary & Key Takeaways' },
      ],
      targetEntities: ['B2B Sales', 'Lead Generation', 'Revenue Growth'],
      proofPoints: ['Grounded in internal knowledge base'],
      internalLinks: [{ page: '/pricing', anchorText: 'transparent pricing' }],
      cta: 'Explore Lead Huntrix capabilities today',
      isAIGenerated: false,
    };

    handleAddBrief(newBrief);
  };

  return (
    <div className="seo-view-container" data-testid="seo-aieo-view">
      {/* HEADER & TABS BAR */}
      <SeoHeader
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onOpenNewBrief={() => handleOpenNewBrief('')}
        onOpenGovernance={() => setIsGovernanceOpen(true)}
        onTriggerAudit={() => setActiveTab('audit')}
      />

      {/* TOP KPI METRICS STRIP */}
      <SeoKpiStrip kpis={kpis} onKpiClick={(targetTab) => setActiveTab(targetTab)} />

      {/* MAIN TAB CONTENT ROUTING */}
      <main className="seo-main-content">
        {activeTab === 'topics' && (
          <TopicResearchTab
            topicClusters={topicClusters}
            onCreateBriefFromTopic={handleCreateBriefFromTopic}
            onAddCluster={handleAddCluster}
            onDeleteCluster={handleDeleteCluster}
            onClearClusters={handleClearClusters}
          />
        )}

        {activeTab === 'briefs' && (
          <ContentBriefsTab
            briefs={briefs}
            onAddBrief={handleAddBrief}
            onApproveBrief={handleApproveBrief}
            onUpdateBrief={handleUpdateBrief}
            onDeleteBrief={handleDeleteBrief}
            onClearBriefs={handleClearBriefs}
            onEnhanceWithAI={handleEnhanceWithAI}
            onAuditDraft={handleAuditDraft}
            onOpenSchema={handleOpenSchemaForBrief}
            onNavigateTab={setActiveTab}
            isEnhancingBrief={isEnhancingBrief}
            onOpenCreateBrief={handleOpenNewBrief}
            isGeneratingBrief={isGeneratingBrief}
            onOpenInStudio={handleOpenInStudio}
            attentionBriefId={attentionBriefId}
            onClearAttention={() => setAttentionBriefId(null)}
          />
        )}

        {activeTab === 'audit' && (
          <OnPageAieoAuditTab
            pageAudit={pageAudit}
            onAuditPage={handleUpdatePageAudit}
            topicClusters={topicClusters}
            briefs={briefs}
            auditContext={auditDraftContext}
            onUpdateBrief={handleUpdateBrief}
            onAddBrief={handleAddBrief}
            onNavigateTab={setActiveTab}
            onOpenInStudio={handleOpenInStudio}
            onPublishToCms={handlePublishToCms}
            governanceSettings={governanceSettings}
            onRequestApproveBrief={handleRequireApprovalFromStudio}
          />
        )}

        {activeTab === 'graph' && (
          <ContentGraphTab
            contentGraph={contentGraph}
            onUpdateGraph={handleUpdateContentGraph}
            briefs={briefs}
            topicClusters={topicClusters}
          />
        )}

        {activeTab === 'refresh' && (
          <ContentRefreshTab
            refreshQueue={refreshQueue}
            onApplyRefresh={handleApplyRefresh}
            onUpdateQueue={handleUpdateRefreshQueue}
            briefs={briefs}
            topicClusters={topicClusters}
            onAuditDraft={handleAuditDraft}
            onOpenInStudio={handleOpenInStudio}
            onUpdateBrief={handleUpdateBrief}
            onAddBrief={handleAddBrief}
            onPublishToCms={handlePublishToCms}
            governanceSettings={governanceSettings}
          />
        )}

        {activeTab === 'visibility' && (
          <VisibilityMonitorTab
            visibilityData={visibilityData}
            onUpdateVisibility={handleUpdateVisibilityData}
            topicClusters={topicClusters}
            briefs={briefs}
          />
        )}
      </main>

      {/* AI CONTENT STUDIO WORKSPACE MODAL (M06 & FR-M11-002) */}
      <ContentStudioModal
        isOpen={Boolean(activeStudioBrief)}
        onClose={() => {
          setActiveStudioBrief(null);
          setStudioScrollToSchema(false);
        }}
        brief={activeStudioBrief}
        onSaveDraft={handleSaveDraftArticle}
        onAuditDraft={handleAuditFromStudio}
        onPublishToCms={handlePublishToCms}
        governanceSettings={governanceSettings}
        onSaveGovernance={handleSaveGovernance}
        onOpenGovernance={() => setIsGovernanceOpen(true)}
        onRequestApproveBrief={handleRequireApprovalFromStudio}
        scrollToSchema={studioScrollToSchema}
        onLinkLandingPage={handleLinkLandingPage}
        onStartSchemaFlow={handleStartSchemaFlow}
        hasAudit={Boolean(pageAudit && (pageAudit.url || pageAudit.targetTopic))}
        onOpenLandingPages={() =>
          navigate('/landing-pages', {
            state: { returnTo: '/seo-aieo', briefId: activeStudioBrief?.id || null },
          })
        }
      />

      {/* PROGRAMMATIC PUBLISHING GOVERNANCE MODAL (FR-M11-009) */}
      <GovernanceSettingsModal
        isOpen={isGovernanceOpen}
        onClose={() => setIsGovernanceOpen(false)}
        initialSettings={governanceSettings}
        onSaveSettings={handleSaveGovernance}
      />

      {/* STRUCTURED DATA & SCHEMA.ORG STUDIO MODAL */}
      <SchemaStudioModal
        isOpen={isSchemaModalOpen}
        onClose={handleCloseSchemaModal}
        schemaTemplates={schemaTemplates}
        briefs={briefs}
        pageAudit={pageAudit}
        topicClusters={topicClusters}
        onUpdateBriefContent={handleUpdateBriefContent}
        onOpenInStudio={handleOpenInStudio}
        initialBriefId={activeSchemaBriefId}
        autoSeedMode={schemaModalSeedMode}
        onAutoUpdated={handleSchemaAutoUpdated}
      />

      {/* CREATE NEW CONTENT BRIEF MODAL */}
      {isCreateBriefOpen && (
        <div className="seo-modal-overlay" onClick={() => setIsCreateBriefOpen(false)}>
          <div className="seo-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="seo-modal-header">
              <div>
                <h3 className="seo-modal-title">
                  <i className="fas fa-file-pen text-blue"></i> Create New Content Brief
                </h3>
              </div>
              <button
                type="button"
                className="seo-modal-close-btn"
                onClick={() => setIsCreateBriefOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveNewBrief}>
              <div className="seo-modal-body">
                <div className="seo-field-group">
                  <label className="seo-field-label">Target Article Title</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="e.g., The Definitive Guide to B2B Data Hygiene in 2026"
                    required
                    value={newBriefTitle}
                    onChange={(e) => setNewBriefTitle(e.target.value)}
                  />
                </div>

                <div className="seo-field-group">
                  <label className="seo-field-label">Target URL Slug</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="e.g., /blog/b2b-data-hygiene-guide"
                    required
                    value={newBriefSlug}
                    onChange={(e) => setNewBriefSlug(e.target.value)}
                  />
                </div>

                <div className="seo-field-group">
                  <label className="seo-field-label">Target Audience &amp; Persona</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="e.g., VP of Sales, Revenue Operations, Founders"
                    value={newBriefAudience}
                    onChange={(e) => setNewBriefAudience(e.target.value)}
                  />
                </div>
              </div>

              <div className="seo-modal-footer">
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={() => setIsCreateBriefOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={(e) => handleSaveNewBrief(e, 'manual')}
                  title="Create clean brief manually without AI"
                  data-testid="create-manual-btn"
                >
                  <i className="fas fa-pen"></i>
                  <span>Manual Draft</span>
                </button>
                <button
                  type="button"
                  className="seo-btn seo-btn-primary"
                  onClick={(e) => handleSaveNewBrief(e, 'ai')}
                  title="Synthesize brief outline and proof points with AI"
                  data-testid="create-ai-btn"
                >
                  <i className="fas fa-robot"></i>
                  <span>AI Generate</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
